import { useEffect, useMemo, useState, type JSX } from 'react';
import { Avatar, Button, Input, Modal, Switch, cn, type PersonneVue } from '@taki/ui';
import { initialesDe } from './sujetsLia.js';

/**
 * CHOISIR DES PERSONNES DE L'ORG (annuaire `PersonnesPort`, les retirees
 * exclues) : pour un message direct, les participants d'un canal prive, le
 * partage d'un sujet de Lia. Une fenetre (G6), une recherche, des cases ; le
 * bouton de validation porte le nombre (G22).
 */
export interface ChoixDePersonnesProps {
  open: boolean;
  titre: string;
  /** Le libelle du bouton, au singulier et au pluriel : « Partager avec 2 personnes ». */
  verbe: string;
  personnes: readonly PersonneVue[];
  /** La personne connectee, jamais proposee. */
  moi: string | null;
  initiales?: readonly string[];
  onClose: () => void;
  onValider: (personIds: string[]) => void | Promise<void>;
  /** Une seule personne a la fois (un message direct a deux). Defaut : plusieurs. */
  erreur?: string | null;
}

export function ChoixDePersonnes({ open, titre, verbe, personnes, moi, initiales = [], onClose, onValider, erreur = null }: ChoixDePersonnesProps): JSX.Element {
  const [recherche, setRecherche] = useState('');
  const [choisis, setChoisis] = useState<Set<string>>(() => new Set(initiales));
  const [enCours, setEnCours] = useState(false);
  const clefInitiale = initiales.join('|');
  useEffect(() => { if (open) { setChoisis(new Set(initiales)); setRecherche(''); setEnCours(false); } }, [open, clefInitiale]);
  const candidates = useMemo(() => {
    const motif = recherche.trim().toLocaleLowerCase('fr');
    return personnes.filter((p) => !p.archive && p.personId !== moi)
      .filter((p) => motif === '' || p.displayName.toLocaleLowerCase('fr').includes(motif) || p.email.toLocaleLowerCase('fr').includes(motif))
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'fr'));
  }, [personnes, recherche, moi]);
  const basculer = (personId: string): void => setChoisis((set) => { const suivant = new Set(set); if (suivant.has(personId)) suivant.delete(personId); else suivant.add(personId); return suivant; });
  const valider = async (): Promise<void> => {
    if (choisis.size === 0 || enCours) return;
    setEnCours(true);
    try { await onValider([...choisis]); } finally { setEnCours(false); }
  };
  return <Modal open={open} onClose={onClose} title={titre} size="sm"
    footer={<><Button variant="secondary" size="sm" onClick={onClose}>Annuler</Button>
      <Button variant="primary" size="sm" onClick={() => void valider()} disabled={choisis.size === 0} loading={enCours}>
        {`${verbe} ${choisis.size === 0 ? '' : choisis.size === 1 ? '1 personne' : `${choisis.size} personnes`}`.trim()}
      </Button></>}>
    <Input value={recherche} onChange={(event) => setRecherche(event.target.value)} placeholder="Nadia Ferreira" aria-label="Rechercher une personne" autoFocus />
    <ul className="mt-2 max-h-64 overflow-y-auto" aria-label="Personnes de l’organisation">
      {candidates.length === 0 && <li className="px-2 py-3 text-sm text-ink-500">{personnes.length === 0 ? 'Annuaire en cours de lecture, ou vide.' : 'Aucune personne ne correspond.'}</li>}
      {candidates.map((p) => <li key={p.personId}>
        <label className={cn('flex h-9 cursor-pointer items-center gap-2.5 rounded-sm px-2 text-sm hover:bg-surface-3', choisis.has(p.personId) && 'bg-surface-3')}>
          <input type="checkbox" className="h-4 w-4 accent-[var(--accent-dot)]" checked={choisis.has(p.personId)} onChange={() => basculer(p.personId)} />
          <Avatar size={22} src={p.photoURL} initials={initialesDe(p.displayName || p.email)} className="bg-surface-3 text-ink-700" />
          <span className="min-w-0 flex-1 truncate text-ink-900">{p.displayName.trim() || p.email}</span>
          <span className="text-xs text-ink-400">{p.statut === 'invite' ? 'invitée' : ''}</span>
        </label>
      </li>)}
    </ul>
    {erreur && <p role="alert" className="mt-2 text-xs text-danger-ink">{erreur}</p>}
  </Modal>;
}

/** La fenetre « Nouveau canal » : un nom, public ou prive, et les participants d'un prive. */
export function FenetreCanal({ open, personnes, moi, onClose, onValider }: {
  open: boolean; personnes: readonly PersonneVue[]; moi: string | null; onClose: () => void;
  onValider: (canal: { nom: string; prive: boolean; participants: string[] }) => Promise<void>;
}): JSX.Element {
  const [nom, setNom] = useState('');
  const [prive, setPrive] = useState(false);
  const [participants, setParticipants] = useState<Set<string>>(() => new Set());
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  useEffect(() => { if (open) { setNom(''); setPrive(false); setParticipants(new Set()); setErreur(null); } }, [open]);
  const valider = async (): Promise<void> => {
    const propre = nom.trim();
    if (propre === '' || enCours) return;
    setEnCours(true); setErreur(null);
    try { await onValider({ nom: propre, prive, participants: [...participants] }); }
    catch (cause) { setErreur(cause instanceof Error ? cause.message : 'Le canal n’a pas pu être créé.'); }
    finally { setEnCours(false); }
  };
  const candidates = personnes.filter((p) => !p.archive && p.personId !== moi).sort((a, b) => a.displayName.localeCompare(b.displayName, 'fr'));
  return <Modal open={open} onClose={onClose} title="Nouveau canal" size="sm"
    footer={<><Button variant="secondary" size="sm" onClick={onClose}>Annuler</Button><Button variant="primary" size="sm" onClick={() => void valider()} disabled={nom.trim() === ''} loading={enCours}>Créer le canal</Button></>}>
    <form onSubmit={(event) => { event.preventDefault(); void valider(); }} className="flex flex-col gap-3">
      <Input value={nom} onChange={(event) => setNom(event.target.value)} maxLength={80} aria-label="Nom du canal" placeholder="projets" autoFocus />
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-ink-900">Canal privé<span className="block text-xs text-ink-500">Seules les personnes choisies le lisent.</span></span>
        <Switch checked={prive} onChange={setPrive} aria-label="Canal privé" />
      </div>
      {prive && <ul className="max-h-48 overflow-y-auto rounded-sm border border-border-soft" aria-label="Participants">
        {candidates.map((p) => <li key={p.personId}>
          <label className="flex h-9 cursor-pointer items-center gap-2.5 px-2 text-sm hover:bg-surface-3">
            <input type="checkbox" className="h-4 w-4" checked={participants.has(p.personId)}
              onChange={() => setParticipants((set) => { const s = new Set(set); if (s.has(p.personId)) s.delete(p.personId); else s.add(p.personId); return s; })} />
            <span className="min-w-0 flex-1 truncate text-ink-900">{p.displayName.trim() || p.email}</span>
          </label>
        </li>)}
      </ul>}
      {erreur && <p role="alert" className="text-xs text-danger-ink">{erreur}</p>}
    </form>
  </Modal>;
}
